import React from 'react';
import TrafficLandingTemplate, { LandingPageConfig } from './TrafficLandingTemplate';
import { Activity, BarChart3, Globe } from 'lucide-react';

const config: LandingPageConfig = {
    slug: 'bn/learn/what-is-social-traffic',
    titleTag: 'What is Social Traffic? The Ultimate Guide 2026 | Traffic Creator',
    metaDescription: 'Understand the difference between social, referral, and organic traffic. Learn how to optimize your traffic sources to improve SEO and site authority.',
    keywords: 'What is Social Traffic, Referral Traffic Definition, Types of Website Traffic, Bot Traffic vs Real Traffic, Educational Traffic Guide',
    primaryKeyword: 'What is Social Traffic',
    secondaryKeyword: 'Referral Traffic Definition',

    reviewCount: '465',
    ratingValue: '4.9',

    h1: 'What Is Social & Referral Traffic? - The Ultimate Educational Guide',
    subheadline: 'If you want to understand referral traffic definitions and what social traffic really means for your website, our guide makes it easier than ever.',

    problemH2: 'Why Confusing Traffic Types is Holding You Back',
    problemText: 'A large part of website analytics is properly attributing where your visitors come from. When you confuse organic traffic, direct traffic, referral traffic, and social traffic, you misallocate marketing budgets and hurt your long-term SEO strategy.',
    solutionPoints: [
        'Clearly define the major types of website traffic: Social, Referral, Direct, and Organic',
        'Learn how bot traffic vs. real traffic impacts your Google Analytics 4 (GA4) data',
        'Understand exactly how Traffic Creator can safely simulate social and referral channels',
        'Discover the SEO benefits of diversifying your traffic sources properly'
    ],

    featuresH2: 'Understanding the Types of Website Traffic',
    features: [
        { icon: <Globe />, title: 'Social Traffic', description: 'What is social traffic? It refers to visitors arriving from platforms like Facebook, Twitter, Instagram, or YouTube. A high percentage shows strong brand presence.' },
        { icon: <Activity />, title: 'Referral Traffic Definition', description: 'Referral traffic is any visit arriving from an external site (excluding search engines and social networks). It indicates your content is trusted and linked heavily.' },
        { icon: <BarChart3 />, title: 'Bot Traffic vs Real Traffic', description: 'Good bots (like Googlebot) crawl your site. Bad bots try to ruin analytics. Real residential traffic mimics human behavior safely to boost metrics.' }
    ],

    testimonialsH2: 'What Our Clients Say About Traffic Creator',
    testimonials: [
        { name: 'David M.', role: 'Digital Marketer', result: 'Balanced Analytics', text: 'I used to struggle with 100% direct traffic. Traffic Creator allowed me to buy targeted referral and social traffic, balancing my GA4 profile beautifully.', seed: 'davidm' },
        { name: 'Sarah K.', role: 'SEO Agency', result: 'Improved Trust', text: 'Understanding the referral traffic definition helped me explain to my clients why they needed diversified traffic sources. Traffic Creator delivers exactly that.', seed: 'sarahk' },
        { name: 'Jessica T.', role: 'Blog Owner', result: 'Safe Traffic', text: 'I was worried about bot traffic vs real traffic until I read this guide. The residential IPs from Traffic Creator are so safe and effective for SEO.', seed: 'jessicat' }
    ],

    ctaH2: 'Ready to Boost Your Social and Referral Metrics?',

    faqs: [
        { question: 'What exactly is social traffic and why do I need it?', answer: 'Social traffic is defined as visitors finding your website from social media platforms. You need it because search engines treat strong social signals and traffic as a metric of authority and popularity.' },
        { question: 'What is the standard referral traffic definition?', answer: 'Referral traffic occurs when a user clicks a link on another site (not a search engine) and lands on yours. It shows your site is being discussed across the web.' },
        { question: 'How is bot traffic different from real residential traffic?', answer: 'Dangerous bot traffic comes from datacenter IPs and shows 100% bounce rates. Real residential traffic uses household IPs, scrolls pages, clicks links, and appears completely natural to analytics.' },
        { question: 'Can Traffic Creator generate social and referral traffic?', answer: 'Yes! Our custom campaigns let you set the referrer. You can enter URLs like facebook.com or Reddit.com so the traffic appears as social or referral in your Google Analytics.' },
        { question: 'Is it safe to buy social and referral traffic?', answer: 'Yes, provided you use high-quality residential IPs (like Traffic Creator uses). We bypass bot detectors and look completely natural, keeping your site safe while boosting its metrics.' }
    ],

    relatedLinks: [
        { label: 'Social Media Traffic Signals', to: '/bn/social-media-traffic-bot' },
        { label: 'Website Traffic Generator', to: '/bn/website-traffic-generator' },
        { label: 'High Quality Traffic', to: '/bn/high-quality-website-traffic' },
    ],

    productName: 'What is Social & Referral Traffic? | The Ultimate Guide 2026',
    productDescription: 'Understand the difference between social, referral, and organic traffic. Learn how to optimize your traffic sources to improve your SEO and site authority.',

    translations: {
        navFeatures: 'বৈশিষ্ট্য',
        navPricing: 'মূল্য',
        navFaq: 'সাধারণ প্রশ্ন',
        navLogin: 'ড্যাশবোর্ড লগইন',
        heroBadge: 'প্রিমিয়াম মানের ট্রাফিক',
        heroCtaPrimary: 'বিনামূল্যে ট্রায়াল শুরু করুন',
        heroCtaSecondary: 'মূল্য দেখুন',
        heroTrust: '10,000+ ব্যবহারকারীর বিশ্বস্ত',
        heroMicrocopy: 'ক্রেডিট কার্ডের প্রয়োজন নেই। 100% নিরাপদ ও Analytics-যাচাইকৃত।',
        heroLiveLabel: 'লাইভ',
        heroTrafficIncrease: 'ট্রাফিক বৃদ্ধি',
        heroActiveLabel: 'সক্রিয়',
        heroVisitorsServed: 'ভিজিটর সেবিত',
        socialProofLabel: 'বিশ্বস্ত:',
        problemWithout: 'ছাড়া',
        problemWith: 'সাথে',
        problemBullets: [
            'কম অর্গানিক ট্রাফিক আপনার বৃদ্ধি সীমিত করে',
            'খারাপ সার্চ র‍্যাঙ্কিং কনভার্সন ক্ষতি করে',
            'প্রতিযোগীরা আপনার কীওয়ার্ড দখল করেছে',
            'উচ্চ বাউন্স রেট SEO ক্ষতি করে',
        ],
        statsCampaigns: 'দৈনিক সক্রিয় ক্যাম্পেইন',
        statsCountries: 'দেশ',
        statsUptime: 'আপটাইম',
        statsSupport: 'সাহায্য',
        featuresSubtitle: 'আপনার ওয়েবসাইটের পারফরম্যান্স এবং সার্চ ইঞ্জিন র‍্যাঙ্কিং বাড়ানোর জন্য সবকিছু।',
        howItWorksH2: '3টি সহজ ধাপ',
        howItWorksSteps: [
            { title: 'ক্যাম্পেইন কনফিগার করুন', desc: 'মিনিটের মধ্যে দেশ, ডিভাইস এবং আচরণ সেটিং নির্বাচন করুন।' },
            { title: 'নেটওয়ার্ক চালু করুন', desc: 'আরাম করুন যখন আমাদের প্ল্যাটফর্ম আপনার URL-এ প্রকৃত ভিজিটর পাঠায়।' },
            { title: 'ফলাফল বিশ্লেষণ করুন', desc: 'Google Analytics 4-এ আপনার মেট্রিক্স উন্নত হতে দেখুন।' },
        ],
        pricingH2: 'স্বচ্ছ',
        pricingSubtitle: 'একবার কেনা। কোনো সাবস্ক্রিপশন নেই। ক্রেডিট কখনো শেষ হয় না।',
        pricingMostPopular: 'সবচেয়ে জনপ্রিয়',
        pricingVisitors: 'ভিজিটর',
        pricingGetStarted: 'শুরু করুন',
        pricingGuarantee: '30-দিনের মানি-ব্যাক গ্যারান্টি',
        pricingDelivery: 'তাৎক্ষণিক ডেলিভারি (~5 মিনিট)',
        pricingCountries: '190+ দেশ',
        faqH2: 'সচরাচর জিজ্ঞাসিত প্রশ্নাবলী',
        ctaSubtitle: '10,000+ গ্রাহকদের সাথে যোগ দিন এবং আজই আপনার ওয়েবসাইট ট্রাফিক বাড়ান।',
        ctaButtonPrimary: 'বিনামূল্যে শুরু করুন',
        ctaButtonSecondary: 'মূল্য দেখুন',
        ctaMicrocopy: 'ক্রেডিট কার্ডের প্রয়োজন নেই। 100% নিরাপদ ও Analytics-যাচাইকৃত।',
        footerDescription: 'বিশ্বব্যাপী SEO পেশাদার এবং ডিজিটাল এজেন্সিদের জন্য সমাধান।',
        footerRelatedServices: 'সম্পর্কিত সেবা',
        footerCompany: 'কোম্পানি',
        footerAbout: 'আমাদের সম্পর্কে',
        footerBlog: 'ব্লগ',
        footerHelpCenter: 'সাহায্য কেন্দ্র',
        footerPrivacy: 'গোপনীয়তা নীতি',
        footerImpressum: 'Impressum',
        footerStatus: 'সকল সিস্টেম চালু',
        footerBuyTraffic: 'ওয়েবসাইট ট্রাফিক কিনুন',
    }
};

const BnWhatIsSocialTraffic: React.FC = () => {
    return <TrafficLandingTemplate config={config} />;
};

export default BnWhatIsSocialTraffic;
